import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import mammoth from 'mammoth';
import { useArticle } from '../../context/ArticleContext';
import { useRole } from '../../context/AuthContext';
import { Icons } from '../common';
import ArticleMetadataPanel from './ArticleMetadataPanel';
import RaiseQueryButton from './RaiseQueryButton';
import './DocumentPreview.css';

const DocumentPreview = ({ file, onBack, onCopyReferences, zipId, zipFile, authors, articleId, onZipModified, onValidationQuerySent }) => {
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedText, setSelectedText] = useState('');
  const [showAuthorsSidebar, setShowAuthorsSidebar] = useState(true);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [downloadDocBuffer, setDownloadDocBuffer] = useState(null); // Buffer for .doc download
  const navigate = useNavigate();
  const { setExtractedContent, articleData } = useArticle();
  const { isAdmin } = useRole();

  useEffect(() => {
    if (file) {
      loadFileContent();
    }
  }, [file]);

  // Handle scroll events to show/hide scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300); // Show button after scrolling 300px
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const loadFileContent = async () => {
    setIsLoading(true);
    setError(null);

    try {
      console.log('Loading file content for:', file.name);
      console.log('File object:', file);
      console.log('ZipEntry exists:', !!file.zipEntry);
      console.log('ZipEntry type:', typeof file.zipEntry);

      if (!file.zipEntry) {
        throw new Error('No zipEntry found in file object');
      }

      const arrayBuffer = await file.zipEntry.async('arraybuffer');
      console.log('ArrayBuffer loaded, size:', arrayBuffer.byteLength);

      if (file.type === 'docx') {
        try {
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setContent(result.value);
          setExtractedContent(result.value);

          // Log any warnings from mammoth
          if (result.messages && result.messages.length > 0) {
            console.warn('Mammoth conversion warnings:', result.messages);
          }
        } catch (docError) {
          console.error('Error converting DOCX:', docError);
          throw new Error('Failed to render the DOCX file. The file may be corrupted.');
        }
      } else if (file.type === 'doc') {
        // Legacy binary .doc — cannot be rendered in browser.
        // Store buffer so the Download button can extract it.
        setDownloadDocBuffer(arrayBuffer);
        setContent('__DOC_LEGACY__'); // Sentinel — actual panel is rendered below in JSX
      } else if (file.type === 'txt') {
        const text = new TextDecoder().decode(arrayBuffer);
        setContent(`<pre>${text}</pre>`);
        setExtractedContent(text);
      } else {
        setError(`Unsupported file type: ${file.type.toUpperCase()}. Supported formats: .docx, .doc, .txt`);
      }
    } catch (err) {
      console.error('Error loading file content:', err);
      console.error('Error details:', err.message);
      console.error('File object at error:', file);

      let errorMessage = 'Failed to load file content';
      if (err.message.includes('zipEntry')) {
        errorMessage = 'File data is not properly loaded. Please try going back and selecting the file again.';
      } else if (err.message.includes('async')) {
        errorMessage = 'Unable to extract file from ZIP archive. The file may be corrupted.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Download the .doc file so the user can open it in Microsoft Word
   */
  const handleDownloadDoc = () => {
    if (!downloadDocBuffer) return;
    const blob = new Blob([downloadDocBuffer], {
      type: 'application/msword'
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = file.name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    setSelectedText(text);
  };

  const processSelectedText = () => {
    if (selectedText) {
      // Check multiple indicators that this is an admin session
      const hasAdminParam = window.location.search.includes('admin=true');
      const hasAdminPath = window.location.pathname.includes('/admin');
      const hasAdminSession = sessionStorage.getItem('adminContext') === 'true';
      const referrerIsAdmin = document.referrer.includes('/admin');

      // Use admin route if any admin indicator is present OR if we're clearly in admin workflow
      const useAdminRoute = isAdmin || hasAdminParam || hasAdminPath || hasAdminSession || referrerIsAdmin ||
        window.location.href.includes('admin') || // Current URL has admin
        document.referrer.includes('admin');      // Came from admin page

      const processRoute = useAdminRoute ? '/admin/process' : '/process';

      navigate(processRoute, {
        state: {
          references: selectedText,
          articleId: articleData?.articleId,
          fromZipProcessor: true,
          manualEntry: false,
          isAdminContext: useAdminRoute, // Pass admin context explicitly
          zipId: zipId // Pass the zipId for auto-completion
        }
      });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="document-preview-container">
      {/* Header */}
      <div className="preview-header">
        <div className="preview-title-section">
          <button onClick={onBack} className="back-button">
            <Icons.ChevronLeftIcon />
          </button>
          <div className="preview-file-info">
            <h2 className="preview-title">
              <span className="file-icon">{file.icon}</span>
              {file.name}
            </h2>
            <p className="preview-subtitle">
              {file.type.toUpperCase()} • Document Preview
            </p>
          </div>
        </div>

        <div className="preview-actions">
          {/* Raise Query Button */}
          <RaiseQueryButton
            queryType="general"
            articleId={articleId}
            zipFile={zipFile}
            buttonText="Raise Query"
            buttonIcon="📧"
            variant="primary"
            size="small"
            onQuerySent={(id) => {
              console.log('Query sent for article:', id);
              // Mark ZIP with validationQuerySent flag for Leena assignment
              if (onValidationQuerySent) {
                onValidationQuerySent(id);
              }
            }}
          />

          {authors && authors.length > 0 && (
            <button
              onClick={() => setShowAuthorsSidebar(!showAuthorsSidebar)}
              className="toggle-sidebar-button"
              title={showAuthorsSidebar ? "Hide Authors" : "Show Authors"}
            >
              <Icons.SettingsIcon />
            </button>
          )}


          {selectedText && (
            <button onClick={processSelectedText} className="process-button selected">
              <Icons.ArrowRightIcon />
              Process Selected
            </button>
          )}
        </div>
      </div>

      {/* Main Content with Sidebar Layout */}
      <div className={`preview-with-sidebar ${!showAuthorsSidebar ? 'sidebar-hidden' : ''}`}>
        {/* Left Side - Document Content */}
        <div className="preview-content-wrapper">
          {isLoading && (
            <div className="preview-loading">
              <div className="loading-spinner"></div>
              <p>Loading document...</p>
            </div>
          )}

          {error && (
            <div className="preview-error">
              <Icons.ExclamationIcon />
              <div>
                <h3>Error Loading Document</h3>
                <p>{error}</p>
                {error.includes('.DOC') && (
                  <div className="error-suggestion">
                    💡 <strong>Tip:</strong> For better compatibility, try converting your .doc file to .docx format using Microsoft Word or an online converter.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Legacy .doc — show download panel with React button */}
          {!isLoading && !error && content === '__DOC_LEGACY__' && (
            <div className="doc-legacy-notice">
              <div className="doc-legacy-icon">📄</div>
              <h3>Legacy .doc Format</h3>
              <p>
                This file is in the old binary <strong>.doc</strong> format
                which cannot be previewed directly in the browser.
              </p>
              <div className="doc-legacy-actions">
                <button
                  className="doc-download-btn"
                  onClick={handleDownloadDoc}
                  title={`Download ${file.name} and open in Microsoft Word`}
                >
                  ⬇️ Download to Open in Word
                </button>
              </div>
              <p className="doc-legacy-hint">
                To insert queries, use <strong>"Insert Query from Database"</strong>
                {' '}on the file list — queries will be added as a separate{' '}
                <code>.txt</code> file inside the ZIP for the TE to review.
              </p>
            </div>
          )}

          {/* Normal HTML preview (.docx / .txt) */}
          {!isLoading && !error && content && content !== '__DOC_LEGACY__' && (
            <div className="preview-content-container">
              <div
                className="preview-content"
                dangerouslySetInnerHTML={{ __html: content }}
                onMouseUp={handleTextSelection}
                onKeyUp={handleTextSelection}
              />
            </div>
          )}

          {/* Instructions */}
          <div className="preview-instructions">
            <div className="instruction-item">
              <span className="instruction-icon">🖱️</span>
              <span>Select text to process specific sections</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-icon">🔄</span>
              <span>Processing will take you directly to the reference processing screen</span>
            </div>
          </div>

          {/* Selection Info */}
          {selectedText && (
            <div className="selection-info">
              <div className="selection-header">
                <span className="selection-icon">✂️</span>
                <span>Selected Text ({selectedText.length} characters)</span>
              </div>
              <div className="selection-preview">
                {selectedText.substring(0, 200)}
                {selectedText.length > 200 && '...'}
              </div>
            </div>
          )}
        </div>

        {/* Right Side - System Authors Sidebar */}
        {showAuthorsSidebar && (
          <div className="authors-sidebar">
            {authors && authors.length > 0 && (
              <ArticleMetadataPanel
                articleId={articleId}
                authors={authors}
                skipApiCall={true}
                alwaysExpanded={true}
                zipFiles={articleData?.zipFiles || []}
                onZipModified={onZipModified}
                onQueryCreated={() => {
                  console.log('Query created for article:', articleId);
                }}
              />
            )}
          </div>
        )}

        {/* Scroll to Top Button */}
        {showScrollToTop && (
          <button
            onClick={scrollToTop}
            className="scroll-to-top-button"
            title="Scroll to top"
            aria-label="Scroll to top"
          >
            <Icons.ChevronUpIcon />
          </button>
        )}
      </div>
    </div>
  );
};

export default DocumentPreview;
